/*
 * Magnum Bundle Builder (<bundle-builder>)
 * ----------------------------------------
 * Client-side "stack" builder. Staging the products the shopper picks, showing a
 * live total and active discount tier, then adding the whole stack to the cart in
 * one /cart/add.js request. If the theme's cart type is "drawer" it refreshes and
 * opens the native cart drawer in place; for "notification"/"page" cart types it
 * redirects to the cart page so the shopper sees the full stack.
 *
 * Self-contained: it only reads its own markup (data-mbb-* hooks) and, for the
 * final add-to-cart, talks to Dawn's existing cart-drawer / cart-notification
 * element through their public renderContents()/getSectionsToRender() API — the
 * same contract product-form.js uses. It defines no globals and touches no other
 * element, so it cannot affect the rest of the theme.
 */
if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
        // variantId -> { variantId, qty, price(cents), title, variantTitle, image }
        this.stack = new Map();
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      }

      connectedCallback() {
        this.itemsEl = this.querySelector('[data-mbb-items]');
        this.emptyEl = this.querySelector('[data-mbb-empty]');
        this.countEl = this.querySelector('[data-mbb-count]');
        this.totalEl = this.querySelector('[data-mbb-total]');
        this.checkoutBtn = this.querySelector('[data-mbb-checkout]');
        this.errorEl = this.querySelector('[data-mbb-error]');
        this.tiers = Array.from(this.querySelectorAll('[data-mbb-tier]'));
        this.tierCodesEnabled = this.dataset.tierCodes === 'true';
        this.countSuffix = (this.countEl?.textContent || '').replace(/^\s*\d+\s*/, '').trim();

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onVariantChange.bind(this));

        if (this.checkoutBtn) {
          this.checkoutBtn.addEventListener('click', this.onCheckout.bind(this));
        }

        // Set each card's initial ADD/price state from its default-selected variant.
        this.querySelectorAll('[data-mbb-variant-select]').forEach((select) => this.syncCard(select));

        this.render();
      }

      onClick(event) {
        const addBtn = event.target.closest('[data-mbb-add]');
        if (addBtn && this.contains(addBtn)) {
          event.preventDefault();
          this.addFromCard(addBtn);
          return;
        }

        const stepBtn = event.target.closest('[data-mbb-step]');
        if (stepBtn && this.contains(stepBtn)) {
          event.preventDefault();
          this.step(stepBtn.dataset.variant, parseInt(stepBtn.dataset.mbbStep, 10));
          return;
        }

        const removeBtn = event.target.closest('[data-mbb-remove]');
        if (removeBtn && this.contains(removeBtn)) {
          event.preventDefault();
          this.removeItem(removeBtn.dataset.variant);
        }
      }

      // Keep the card's displayed price + ADD button in sync with the chosen variant.
      onVariantChange(event) {
        const select = event.target.closest('[data-mbb-variant-select]');
        if (!select || !this.contains(select)) return;
        this.syncCard(select);
      }

      // Reflect the selected variant on a card: update the shown price and
      // disable ADD when the selected variant is sold out.
      syncCard(select) {
        const card = select.closest('[data-mbb-card]');
        if (!card) return;
        const opt = select.selectedOptions[0];
        if (!opt) return;
        const priceEl = card.querySelector('[data-mbb-card-price]');
        if (priceEl) priceEl.innerHTML = this.formatMoney(opt.dataset.price);
        const addBtn = card.querySelector('[data-mbb-add]');
        if (addBtn) addBtn.disabled = String(opt.dataset.available) !== 'true';
        this.updateCardBadge(card);
      }

      readCard(addBtn) {
        const card = addBtn.closest('[data-mbb-card]');
        const select = card ? card.querySelector('[data-mbb-variant-select]') : null;
        let source = addBtn.dataset;
        if (select && select.selectedOptions[0]) {
          const opt = select.selectedOptions[0];
          source = {
            variantId: opt.value,
            price: opt.dataset.price,
            available: opt.dataset.available,
            variantTitle: opt.dataset.variantTitle,
          };
        }
        return {
          variantId: String(source.variantId || ''),
          price: parseInt(source.price, 10) || 0,
          available: String(source.available) === 'true',
          variantTitle: source.variantTitle || '',
          title: addBtn.dataset.productTitle || '',
          image: addBtn.dataset.productImage || '',
        };
      }

      addFromCard(addBtn) {
        const item = this.readCard(addBtn);
        if (!item.variantId || !item.available) return;

        const existing = this.stack.get(item.variantId);
        if (existing) {
          existing.qty += 1;
        } else {
          this.stack.set(item.variantId, {
            variantId: item.variantId,
            qty: 1,
            price: item.price,
            title: item.title,
            variantTitle: item.variantTitle,
            image: item.image,
          });
        }
        this.clearError();
        this.render();
      }

      step(variantId, delta) {
        const item = this.stack.get(variantId);
        if (!item) return;
        item.qty += delta;
        if (item.qty <= 0) {
          this.stack.delete(variantId);
        }
        this.render();
      }

      // Named removeItem (not remove) so it never shadows the native
      // Element.prototype.remove() on the custom element instance.
      removeItem(variantId) {
        this.stack.delete(variantId);
        this.render();
      }

      totalItems() {
        let n = 0;
        this.stack.forEach((item) => {
          n += item.qty;
        });
        return n;
      }

      totalCents() {
        let cents = 0;
        this.stack.forEach((item) => {
          cents += item.price * item.qty;
        });
        return cents;
      }

      render() {
        const count = this.totalItems();
        const isEmpty = this.stack.size === 0;

        // Item rows (rebuilt each render; the list is short).
        this.querySelectorAll('[data-mbb-row]').forEach((row) => row.remove());
        this.stack.forEach((item) => {
          this.itemsEl.appendChild(this.buildRow(item));
        });

        if (this.emptyEl) this.emptyEl.hidden = !isEmpty;
        if (this.countEl) this.countEl.textContent = `${count} ${this.countSuffix}`.trim();
        if (this.itemsEl) this.itemsEl.classList.toggle('mbb__items--scrollable', this.stack.size > 3);
        if (this.totalEl) this.totalEl.innerHTML = this.formatMoney(this.totalCents());
        if (this.checkoutBtn) this.checkoutBtn.disabled = isEmpty;

        this.updateCardBadges();
        this.updateTiers(count);
      }

      selectedVariantIdForCard(card) {
        if (!card) return '';
        const select = card.querySelector('[data-mbb-variant-select]');
        if (select && select.selectedOptions[0]) return String(select.selectedOptions[0].value || '');
        const addBtn = card.querySelector('[data-mbb-add]');
        return addBtn ? String(addBtn.dataset.variantId || '') : '';
      }

      updateCardBadge(card) {
        const badge = card ? card.querySelector('[data-mbb-card-added]') : null;
        const meta = card ? card.querySelector('[data-mbb-card-meta]') : null;
        if (!badge) return;
        const variantId = this.selectedVariantIdForCard(card);
        const item = variantId ? this.stack.get(variantId) : null;
        const qty = item ? item.qty : 0;
        badge.hidden = qty <= 0;
        if (meta) meta.hidden = qty <= 0;
        badge.textContent = qty > 0 ? `${qty} Added` : '';
      }

      updateCardBadges() {
        this.querySelectorAll('[data-mbb-card]').forEach((card) => this.updateCardBadge(card));
      }

      // Built entirely with DOM methods + textContent so no product-supplied
      // string is ever parsed as HTML. innerHTML is used only for the trusted
      // shop money_format string, which may legitimately contain markup.
      buildRow(item) {
        const row = document.createElement('div');
        row.className = 'mbb__item';
        row.setAttribute('data-mbb-row', '');

        // Media
        let media;
        if (item.image) {
          media = document.createElement('img');
          media.className = 'mbb__item-img';
          media.src = item.image;
          media.alt = '';
          media.loading = 'lazy';
          media.width = 48;
          media.height = 48;
        } else {
          media = document.createElement('span');
          media.className = 'mbb__item-img mbb__item-img--placeholder';
          media.setAttribute('aria-hidden', 'true');
        }
        row.appendChild(media);

        // Info column
        const info = document.createElement('div');
        info.className = 'mbb__item-info';

        const title = document.createElement('span');
        title.className = 'mbb__item-title';
        title.textContent = item.title;
        info.appendChild(title);

        if (item.variantTitle && item.variantTitle !== 'Default Title') {
          const variant = document.createElement('span');
          variant.className = 'mbb__item-variant';
          variant.textContent = item.variantTitle;
          info.appendChild(variant);
        }

        const price = document.createElement('span');
        price.className = 'mbb__item-price';
        price.innerHTML = this.formatMoney(item.price * item.qty);
        info.appendChild(price);
        row.appendChild(info);

        // Quantity stepper
        const qty = document.createElement('div');
        qty.className = 'mbb__item-qty';
        qty.appendChild(this.qtyButton('-1', '−', 'Decrease quantity', item.variantId));
        const qtyNum = document.createElement('span');
        qtyNum.className = 'mbb__qty-num';
        qtyNum.setAttribute('aria-live', 'polite');
        qtyNum.textContent = item.qty;
        qty.appendChild(qtyNum);
        qty.appendChild(this.qtyButton('1', '+', 'Increase quantity', item.variantId));
        row.appendChild(qty);

        // Remove
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'mbb__item-remove';
        remove.setAttribute('data-mbb-remove', '');
        remove.setAttribute('data-variant', item.variantId);
        remove.setAttribute('aria-label', `Remove ${item.title}`);
        remove.textContent = '×';
        row.appendChild(remove);

        return row;
      }

      qtyButton(step, glyph, label, variantId) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mbb__qty-btn';
        btn.setAttribute('data-mbb-step', step);
        btn.setAttribute('data-variant', variantId);
        btn.setAttribute('aria-label', label);
        btn.textContent = glyph;
        return btn;
      }

      updateTiers(count) {
        this.tiers.forEach((tier) => {
          const min = parseInt(tier.dataset.min, 10) || 0;
          const maxRaw = parseInt(tier.dataset.max, 10);
          const max = Number.isNaN(maxRaw) || maxRaw <= 0 ? Infinity : maxRaw;
          const active = count >= min && count <= max;
          tier.classList.toggle('is-active', active);
        });
      }

      // Single STACK code — the Shopify Function reads each line's _stack_source
      // tag and applies the correct tier % from the total tagged quantity, so the
      // theme no longer picks a per-tier code (STACK5/10/15). Returns 'STACK' once
      // an active tier is reached, else ''.
      //
      // Deliberately does NOT test tier.dataset.code. Those values are the retired
      // STACK5/10/15 strings and are pure truthiness now, but the setting's help text
      // used to tell merchants the code "must exist in Shopify > Discounts" — so
      // clearing three strings that name deactivated discounts would have silently
      // stopped the Bundle Builder applying STACK at checkout. Qualification is a
      // question of quantity, not of a leftover string being non-empty.
      activeTierCode() {
        let qualifies = false;
        this.tiers.forEach((tier) => {
          if (!tier.classList.contains('is-active')) return;
          qualifies = true;
        });
        return qualifies ? 'STACK' : '';
      }

      async onCheckout() {
        if (this.stack.size === 0 || this.checkoutBtn.disabled) return;

        // Capture the focus target before setLoading() disables (and blurs) the
        // button, so the cart drawer can return focus somewhere meaningful.
        const opener = document.activeElement;

        // Only the cart DRAWER can be refreshed in-place for a multi-line add:
        // its renderContents() re-renders whole fixed sections (#CartDrawer, cart
        // bubble). The cart NOTIFICATION popup is single-product-oriented (it looks
        // up a cart-notification-product-{id} element that a bundle add has no single
        // id for), so on notification/page cart themes we send the shopper to the
        // cart page to review the full stack instead. This adapts to whatever cart
        // type the merchant has set in Theme settings.
        const drawer = document.querySelector('cart-drawer');
        const useDrawer = !!drawer && typeof drawer.renderContents === 'function';

        const items = [];
        this.stack.forEach((item) => {
          // Stamp every line added through the Bundle Builder with its source, so
          // the STACK tier discount can be tied to (and audited against) this flow
          // only. Regular product-page adds never carry this property. The leading
          // underscore hides it from the storefront cart while keeping it on the order.
          items.push({
            id: Number(item.variantId),
            quantity: item.qty,
            properties: { _stack_source: 'build-your-stack' },
          });
        });

        const body = { items };
        if (useDrawer && typeof drawer.getSectionsToRender === 'function') {
          body.sections = drawer.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        this.setLoading(true);
        this.clearError();

        try {
          const response = await fetch(`${window.routes.cart_add_url}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify(body),
          });
          const data = await response.json();

          if (data.status) {
            this.showError(data.description || data.message || 'Unable to add to cart.');
            return;
          }

          // Auto-apply the active tier's discount code (toggle on + code set).
          // Items are already in the cart, so route through Shopify's
          // /discount/<code> handler, which applies the code and redirects to
          // the cart with it active. Overrides the drawer/redirect path.
          const tierCode = this.tierCodesEnabled ? this.activeTierCode() : '';
          if (tierCode) {
            const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            const base = root.replace(/\/$/, '');
            const cartUrl = (window.routes && window.routes.cart_url) || base + '/cart';
            window.location.href =
              base + '/discount/' + encodeURIComponent(tierCode) + '?redirect=' + encodeURIComponent(cartUrl);
            return;
          }

          if (useDrawer) {
            drawer.setActiveElement?.(opener);
            drawer.classList.remove('is-empty');
            drawer.renderContents(data);
          } else {
            window.location = window.routes.cart_url;
            return;
          }

          this.stack.clear();
          this.render();
        } catch (error) {
          console.error('[bundle-builder] add to cart failed', error);
          this.showError('Something went wrong. Please try again.');
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(loading) {
        if (!this.checkoutBtn) return;
        this.checkoutBtn.classList.toggle('loading', loading);
        this.checkoutBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
        const spinner = this.checkoutBtn.querySelector('.mbb__checkout-spinner');
        const text = this.checkoutBtn.querySelector('.mbb__checkout-text');
        if (spinner) spinner.classList.toggle('hidden', !loading);
        if (text) text.classList.toggle('hidden', loading);
        // While loading, force-disable. When loading ends (success OR error),
        // restore the real state: disabled only when the stack is empty. This
        // prevents the button getting stuck disabled after a failed add-to-cart
        // (the error paths return before render() runs).
        this.checkoutBtn.disabled = loading || this.stack.size === 0;
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      // Classic Shopify money formatter (mirrors Liquid's `| money`), driven by the
      // shop's money_format embedded on the element.
      formatMoney(cents) {
        let value = cents;
        if (typeof value === 'string') value = value.replace('.', '');
        value = parseInt(value, 10) || 0;

        const format = this.moneyFormat || '${{amount}}';
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;

        const withDelimiters = (number, precision, thousands, decimal) => {
          precision = precision == null ? 2 : precision;
          thousands = thousands == null ? ',' : thousands;
          decimal = decimal == null ? '.' : decimal;
          if (isNaN(number) || number == null) return '0';
          const fixed = (number / 100).toFixed(precision);
          const parts = fixed.split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          const centsPart = parts[1] ? decimal + parts[1] : '';
          return dollars + centsPart;
        };

        const match = format.match(placeholder);
        const token = match ? match[1] : 'amount';
        let formatted;
        switch (token) {
          case 'amount':
            formatted = withDelimiters(value, 2);
            break;
          case 'amount_no_decimals':
            formatted = withDelimiters(value, 0);
            break;
          case 'amount_with_comma_separator':
            formatted = withDelimiters(value, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            formatted = withDelimiters(value, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            formatted = withDelimiters(value, 2, "'", '.');
            break;
          case 'amount_no_decimals_with_space_separator':
            formatted = withDelimiters(value, 0, ' ', '');
            break;
          case 'amount_with_space_separator':
            formatted = withDelimiters(value, 2, ' ', ',');
            break;
          default:
            formatted = withDelimiters(value, 2);
        }
        return format.replace(placeholder, formatted);
      }
    }
  );
}
